/* eslint-disable jsx-a11y/role-supports-aria-props */
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VirtualizedAutocomplete from '../../../components/Autocomplete';
import { isFieldLocked } from '../../../utils/fieldUtils';
import { InputAdornment, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { abortController } from '../../../utils/abortController';
import useUser from '../../../hooks/useUser';
import useSessionExpire from '../../../hooks/useSessionExpire';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';

const LinkedSellingPaymentTerms = forwardRef(({
    isWritable = false,
    isListDataLoading = false,
    paymentTermTypes = [],
    selectedPFI = null,
    selectedBusinessUnit = null,
    isPeriodLocked = false,
    lockedFields = [],
    dealHeaderFormData = {},
    dealValue = 0,
    expandedFormSections = [],
    setExpandedFormSections = () => { },
    setShowAlert = () => { },
    setAlertMessage = () => { },
    setAlertType = () => { },
    logOut = () => { },
    onSblcInvalidSelect = () => { },
    onSblcLimitExceeded = () => { },
}, ref) => {

    const currentUser = useUser();
    const triggerSessionExpire = useSessionExpire();
    const formRef = useRef(null);
    const isFirstSblcFetch = useRef(true);

    const formDataSchema = useMemo(() => ({
        id: null,
        payment_term_type_id: null,
        advance_percentage: null,
        remaining_balance_percentage: null,
        tenor_type: null,
        lc_type: null,
        usance_days: null,
        sblc_id: null,
        advance_amount: null,
        from_date_field: null,
    }), []);

    const allFromDateOptions = useMemo(() => [
        { id: "bl_date", value: "BL Date" },
        { id: "copy_docs", value: "Copy Docs" },
        { id: "acceptance", value: "Acceptance" },
    ], []);

    // ── Internal form state ───────────────────────────────────────────────────
    const [formData, setFormData] = useState(structuredClone(formDataSchema));
    const [isExisting, setIsExisting] = useState(false);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    // ── SBLC list ─────────────────────────────────────────────────────────────
    const [sblcList, setSblcList] = useState([]);
    const [isSblcListLoading, setIsSblcListLoading] = useState(false);

    // ── Modal state ───────────────────────────────────────────────────────────
    const [showSblcWarningModal, setShowSblcWarningModal] = useState(false);
    const [sblcWarningMessage, setSblcWarningMessage] = useState("");
    const [showSblcInvalidModal, setShowSblcInvalidModal] = useState(false);

    // ── Derived ───────────────────────────────────────────────────────────────
    const selectedType = paymentTermTypes.find(t => t.id === formData.payment_term_type_id) || null;
    const ptID = selectedType?.id ?? null;

    const isMixedPayment = ptID === 2;
    const isLC = ptID === 3;
    const isSBLC = ptID === 4;
    const isOpenAccount = ptID === 5;
    const isCollection = ptID === 6;

    const tenorType = (formData.tenor_type || "").toUpperCase();
    const lcType = (formData.lc_type || "").toUpperCase();

    const mixedTenorOptions = useMemo(() => ["LC", "Open Account"], []);
    const mixedLCTypeOptions = useMemo(() => ["Sight", "Usance"], []);
    const lcTenorOptions = useMemo(() => ["Sight", "Usance", "Mixed"], []);
    const collectionTypeOptions = useMemo(() => ["D/P", "D/A"], []);

    const fromDateOptionsWithCopyDocs = useMemo(() => allFromDateOptions, [allFromDateOptions]);
    const fromDateOptionsNoCopyDocs = useMemo(() =>
        allFromDateOptions.filter(o => o.id !== "copy_docs"), [allFromDateOptions]);

    const autoRemainingPct = useMemo(() => {
        const val = parseFloat(formData.advance_percentage);
        return (!isNaN(val) && val >= 0 && val <= 100) ? (100 - val).toFixed(2) : "";
    }, [formData.advance_percentage]);

    const sblcOptionsWithDisabled = useMemo(() =>
        sblcList.map(option => ({
            ...option,
            _disabled: false,
        })),
        [sblcList]
    );

    // ── FETCH PAYMENT TERM DETAIL ─────────────────────────────────────────────
    useEffect(() => {
        if (!selectedPFI) {
            setFormData(structuredClone(formDataSchema));
            setIsExisting(false);
            return;
        }

        const fetchDetail = async () => {
            setIsDetailLoading(true);
            try {
                const response = await axios({
                    method: "post",
                    url: "/api/GetPaymentTermDetail",
                    data: {
                        Business_Unit_Id: selectedBusinessUnit,
                        deal_pfi_id: selectedPFI,
                        payment_term_leg: "Selling",
                        user_id: currentUser?.id,
                    },
                    signal: abortController.signal,
                });

                const { status, data } = response;
                if (status === 200 && data?.length > 0) {
                    const record = data[0];
                    const loaded = {
                        id: record.id,
                        payment_term_type_id: record.payment_term_type_id,
                        advance_percentage: record.advance_percentage,
                        remaining_balance_percentage: record.remaining_balance_percentage,
                        tenor_type: record.tenor_type,
                        lc_type: record.lc_type,
                        usance_days: record.usance_days,
                        sblc_id: record.sblc_id,
                        advance_amount: record.advance_amount,
                        from_date_field: allFromDateOptions.find(o => o.id === record.from_date_field) ?? null,
                    };
                    setFormData(structuredClone(loaded));
                    setIsExisting(true);
                } else {
                    setFormData(structuredClone(formDataSchema));
                    setIsExisting(false);
                }
            } catch (error) {
                if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
                console.error("GetPaymentTermDetail (Linked Selling) error:", error);
                handleApiError(error);
            } finally {
                setIsDetailLoading(false);
            }
        };

        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPFI]);

    // ── FETCH SBLC LIST ───────────────────────────────────────────────────────
    useEffect(() => {

        const fetchSblcList = async () => {
            if (isFirstSblcFetch.current) {
                isFirstSblcFetch.current = false;
            } else {
                setFormData(prev => ({ ...prev, sblc_id: null })); // only reset on actual change
            }
            setIsSblcListLoading(true);
            try {
                const response = await axios({
                    method: "post",
                    url: "/api/v2/GetSBLCList",
                    data: {
                        sblc_leg: "Selling",
                        vendor_id: dealHeaderFormData?.Buyer_Id?.id ?? null,
                    },
                    signal: abortController.signal,
                });
                if (response.status === 200) {
                    setSblcList(response.data?.sblc_list || []);
                }
            } catch (error) {
                if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
                console.error("GetSBLCList (Linked Selling) error:", error);
            } finally {
                setIsSblcListLoading(false);
            }
        };

        if (dealHeaderFormData?.Buyer_Id?.id) {
            fetchSblcList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealHeaderFormData?.Buyer_Id?.id]);

    // ── Warn when deal value exceeds SBLC limit ───────────────────────────────
    useEffect(() => {
        if (!isSBLC || !formData.sblc_id) return;
        const sblcIdValue = formData.sblc_id?.id ?? formData.sblc_id;
        const selectedSblc = sblcList.find(s => s.id === sblcIdValue);
        if (!selectedSblc) return;
        const limit = parseFloat(selectedSblc.limit);
        if (!isNaN(limit) && dealValue > limit) {
            const excess = (dealValue - limit).toLocaleString();
            setSblcWarningMessage(
                `The selling deal value (${dealValue.toLocaleString()}) has exceeded the selected SBLC limit (${limit.toLocaleString()}). The SBLC balance has gone into negative by ${excess}. Please review your selection.`
            );
            setShowSblcWarningModal(true);
            onSblcLimitExceeded("Selling", dealValue, limit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealValue]);

    const handleApiError = useCallback((error) => {
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
        const status = error?.response?.status;
        if (status === 403) { triggerSessionExpire(); return; }
        setAlertMessage(
            status === 401 ? "Unauthorized access."
                : status === 429 ? "Request limit exceeded."
                    : "An error occurred. Please try again."
        );
        setAlertType("error");
        setShowAlert(true);
        if (status === 429) setTimeout(logOut, 3000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const locked = (fieldName) =>
        Boolean(isPeriodLocked && isFieldLocked(fieldName, "Payment Terms", "Pre-Shipment", lockedFields));

    const LockAdornment = ({ fieldName }) =>
        locked(fieldName) ? (
            <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                <Tooltip title="This field is locked and cannot be edited due to period lock." arrow placement="top">
                    <InfoIcon fontSize="small" sx={{ cursor: "default" }} />
                </Tooltip>
            </InputAdornment>
        ) : null;

    const numericKeyDown = (e) => {
        if (["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(e.key)) e.preventDefault();
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: typeof value === "string"
                ? (value.trim() === "" ? null : value)
                : (value ?? null),
        }));
    };

    const handleDaysChange = (e) => {
        const raw = e.target.value;
        if (raw === "") { handleChange("usance_days", null); return; }
        const v = parseInt(raw);
        if (!isNaN(v) && v >= 0) handleChange("usance_days", v);
    };

    const handleSblcChange = (value) => {
        handleChange("sblc_id", value);
        if (value && value.isValid === false) {
            setShowSblcInvalidModal(true);
            onSblcInvalidSelect("Selling");
        }
    };

    const resetSubFields = useCallback(() => {
        setFormData(prev => ({
            ...prev,
            advance_percentage: null,
            remaining_balance_percentage: null,
            tenor_type: null,
            lc_type: null,
            usance_days: null,
            sblc_id: null,
            advance_amount: null,
            from_date_field: null,
        }));
    }, []);

    const renderSblcOption = (props, option) => {
        const isInvalid = option.isValid === false;
        return (
            <Tooltip key={option.id} title={isInvalid ? `This SBLC's validity date has passed. You may still select it.` : ""} placement="right" arrow>
                <span style={{ display: "block" }}>
                    <li {...props} style={{ ...props.style }}>
                        {option.value}
                        {isInvalid && <span style={{ marginLeft: 6, fontSize: "0.75em", color: "#e65100" }}>(Expired)</span>}
                    </li>
                </span>
            </Tooltip>
        );
    };

    const FromDateDropdown = ({ options }) => (
        <Grid size={{ xs: 6 }}>
            <VirtualizedAutocomplete
                isMultiple={false} isObject={true} isRequired={true}
                isWritable={isWritable} isLocked={locked("from_date_field")}
                filterOn="Calculate From" options={options}
                selectedOptions={formData.from_date_field || null}
                handleSelectChange={(_, v) => handleChange("from_date_field", v)}
            />
        </Grid>
    );

    const buildPayload = () => ({
        Business_Unit_Id: selectedBusinessUnit,
        deal_pfi_id: selectedPFI,
        payment_term_leg: "Selling",
        payment_term_type_id: formData.payment_term_type_id,
        advance_amount: formData.advance_amount ?? null,
        advance_percentage: formData.advance_percentage ?? null,
        remaining_balance_percentage: autoRemainingPct !== "" ? parseFloat(autoRemainingPct) : null,
        tenor_type: formData.tenor_type ?? null,
        lc_type: formData.lc_type ?? null,
        usance_days: formData.usance_days ?? null,
        from_date_field: formData.from_date_field?.id ?? null,
        sblc_id: formData.sblc_id?.id ?? null,
        deal_value: dealValue,
        vendor_id: dealHeaderFormData?.Buyer_Id?.id ?? null,
        user_id: currentUser?.id,
    });

    const isValid = () => {
        if (!formData.payment_term_type_id) return true;
        return formRef.current?.reportValidity() ?? true;
    };

    const handleSubmit = () => {
        if (!formData.payment_term_type_id) return Promise.resolve(true);
        if (!isValid()) return Promise.resolve(false);

        return new Promise((resolve, reject) => {
            setShowAlert(false);
            setAlertMessage("");
            setAlertType("");

            const payload = buildPayload();
            if (isExisting) payload.id = formData.id;

            const url = isExisting
                ? "/api/UpdatePaymentTermDetailSelling"
                : "/api/AddPaymentTermDetailSelling";

            axios({ method: "post", url, data: payload, signal: abortController.signal })
                .then(({ status, data: respData }) => {
                    if (status === 200) {
                        if (!isExisting && respData?.id) {
                            setFormData(prev => ({ ...prev, id: respData.id }));
                            setIsExisting(true);
                        }
                        resolve(true);
                    } else {
                        setAlertMessage(respData?.error || "An error occurred. Please try again.");
                        setAlertType("error");
                        setShowAlert(true);
                        reject(false);
                    }
                })
                .catch((error) => {
                    handleApiError(error);
                    reject(false);
                });
        });
    };

    useImperativeHandle(ref, () => ({
        isValid,
        handleSubmit,
        getPaymentTermTypeId: () => formData.payment_term_type_id,
        getSblcId: () => formData.sblc_id,
    }));

    const handleAccordionChange = (section) => (_, isExpanded) => {
        setExpandedFormSections(
            isExpanded
                ? [...expandedFormSections, section]
                : expandedFormSections.filter(id => id !== section)
        );
    };

    return (
        <>
            <Accordion
                sx={{ marginRight: "0.1em !important", marginLeft: "0 !important" }}
                expanded={expandedFormSections.includes(7)}
                onChange={handleAccordionChange(7)}
            >
                <AccordionSummary
                    id="linked-selling-payment-terms-header"
                    aria-controls="linked-selling-payment-terms-content"
                    expandIcon={<ExpandMoreIcon />}
                    slot="div"
                >
                    <small className="page-heading title">Selling Payment Terms</small>
                </AccordionSummary>

                <AccordionDetails>
                    <form ref={formRef} className="deal-form mt-1" onSubmit={(e) => e.preventDefault()}>
                        <Grid container spacing={"10px"} className="align-items-start autocomplete">

                            <Grid size={{ xs: 6 }}>
                                <VirtualizedAutocomplete
                                    isLoading={isDetailLoading || isListDataLoading}
                                    isMultiple={false} isObject={true} isRequired={true}
                                    isWritable={isWritable} isLocked={locked("payment_term_type_id")}
                                    filterOn="Payment Terms" options={paymentTermTypes}
                                    useRawOptionLabels={true} selectedOptions={selectedType}
                                    handleSelectChange={(_, v) => {
                                        handleChange("payment_term_type_id", v?.id ?? null);
                                        resetSubFields();
                                    }}
                                />
                            </Grid>

                            {isMixedPayment && (
                                <>
                                    <Grid size={{ xs: 6 }} className="form-text-field">
                                        <TextField
                                            label="Advance %" type="number" required
                                            disabled={locked("advance_percentage")}
                                            value={formData.advance_percentage ?? ""}
                                            onChange={(e) => {
                                                const v = parseFloat(e.target.value);
                                                if (e.target.value === "" || (v >= 0 && v <= 100))
                                                    handleChange("advance_percentage", e.target.value === "" ? null : v);
                                            }}
                                            size="small"
                                            slotProps={{ input: { readOnly: !isWritable, endAdornment: <LockAdornment fieldName="advance_percentage" /> } }}
                                            onKeyDown={numericKeyDown}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6 }} className="form-text-field">
                                        <TextField label="Remaining Balance % (AUTO)" type="number" value={autoRemainingPct} disabled size="small" slotProps={{ input: { readOnly: true } }} />
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <VirtualizedAutocomplete
                                            isMultiple={false} isObject={false} isWritable={isWritable}
                                            isLocked={locked("tenor_type")} filterOn="Tenor"
                                            options={mixedTenorOptions} selectedOptions={formData.tenor_type || null}
                                            handleSelectChange={(_, v) => {
                                                handleChange("tenor_type", v);
                                                setFormData(prev => ({ ...prev, lc_type: null, usance_days: null, from_date_field: null }));
                                            }}
                                        />
                                    </Grid>
                                    {tenorType === "LC" && (
                                        <>
                                            <Grid size={{ xs: 6 }}>
                                                <VirtualizedAutocomplete
                                                    isMultiple={false} isObject={false} isWritable={isWritable}
                                                    isRequired={true} isLocked={locked("lc_type")} filterOn="LC Tenor"
                                                    options={mixedLCTypeOptions} selectedOptions={formData.lc_type || null}
                                                    handleSelectChange={(_, v) => {
                                                        handleChange("lc_type", v);
                                                        setFormData(prev => ({ ...prev, usance_days: null, from_date_field: null }));
                                                    }}
                                                />
                                            </Grid>
                                            {lcType === "USANCE" && (
                                                <>
                                                    <Grid size={{ xs: 6 }} className="form-text-field">
                                                        <TextField label="Usance Days" type="number" required disabled={locked("usance_days")} value={formData.usance_days ?? ""} onChange={handleDaysChange} size="small" slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }} onKeyDown={numericKeyDown} />
                                                    </Grid>
                                                    <FromDateDropdown options={fromDateOptionsNoCopyDocs} />
                                                </>
                                            )}
                                        </>
                                    )}
                                    {tenorType === "OPEN ACCOUNT" && (
                                        <>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField label="Days for Remaining Balance" type="number" required disabled={locked("usance_days")} value={formData.usance_days ?? ""} onChange={handleDaysChange} size="small" slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }} onKeyDown={numericKeyDown} />
                                            </Grid>
                                            <FromDateDropdown options={fromDateOptionsWithCopyDocs} />
                                        </>
                                    )}
                                </>
                            )}

                            {isLC && (
                                <>
                                    <Grid size={{ xs: 6 }}>
                                        <VirtualizedAutocomplete
                                            isMultiple={false} isObject={false} isWritable={isWritable}
                                            isLocked={locked("tenor_type")} filterOn="Tenor"
                                            options={lcTenorOptions} selectedOptions={formData.tenor_type || null}
                                            handleSelectChange={(_, v) => {
                                                handleChange("tenor_type", v);
                                                setFormData(prev => ({ ...prev, lc_type: null, usance_days: null, advance_percentage: null, remaining_balance_percentage: null, from_date_field: null }));
                                            }}
                                        />
                                    </Grid>
                                    {tenorType === "USANCE" && (
                                        <>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField label="Usance Days" type="number" required disabled={locked("usance_days")} value={formData.usance_days ?? ""} onChange={handleDaysChange} size="small" slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }} onKeyDown={numericKeyDown} />
                                            </Grid>
                                            <FromDateDropdown options={fromDateOptionsNoCopyDocs} />
                                        </>
                                    )}
                                    {tenorType === "MIXED" && (
                                        <>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField label="Sight %" type="number" required disabled={locked("advance_percentage")} value={formData.advance_percentage ?? ""} onChange={(e) => { const v = parseFloat(e.target.value); if (e.target.value === "" || (v >= 0 && v <= 100)) handleChange("advance_percentage", e.target.value === "" ? null : v); }} size="small" slotProps={{ input: { readOnly: !isWritable, endAdornment: <LockAdornment fieldName="advance_percentage" /> } }} onKeyDown={numericKeyDown} />
                                            </Grid>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField label="Usance % (AUTO)" type="number" value={autoRemainingPct} disabled size="small" slotProps={{ input: { readOnly: true } }} />
                                            </Grid>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField label="Remaining Balance Usance Days" type="number" required disabled={locked("usance_days")} value={formData.usance_days ?? ""} onChange={handleDaysChange} size="small" slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }} onKeyDown={numericKeyDown} />
                                            </Grid>
                                            <FromDateDropdown options={fromDateOptionsNoCopyDocs} />
                                        </>
                                    )}
                                </>
                            )}

                            {isSBLC && (
                                <>
                                    <Grid size={{ xs: 6 }}>
                                        <VirtualizedAutocomplete
                                            isLoading={isSblcListLoading}
                                            isMultiple={false} isObject={true} isWritable={isWritable}
                                            isLocked={locked("sblc_id")} filterOn="Select Selling SBLC"
                                            options={sblcOptionsWithDisabled} selectedOptions={formData.sblc_id || null}
                                            handleSelectChange={(_, v) => handleSblcChange(v)}
                                            renderOption={renderSblcOption}
                                        />
                                    </Grid>
                                    {/* {dealValue > 0 && (
                                        <Grid size={{ xs: 6 }} sx={{ display: "flex", alignItems: "center" }}>
                                            <Typography variant="caption" sx={{ color: "#666" }}>
                                                Deal value (Selling):&nbsp;<strong>{dealValue.toLocaleString()}</strong>
                                            </Typography>
                                        </Grid>
                                    )} */}
                                </>
                            )}

                            {isOpenAccount && (
                                <>
                                    <Grid size={{ xs: 6 }} className="form-text-field">
                                        <TextField label="Days for Payment" type="number" required disabled={locked("usance_days")} value={formData.usance_days ?? ""} onChange={handleDaysChange} size="small" slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }} onKeyDown={numericKeyDown} />
                                    </Grid>
                                    <FromDateDropdown options={fromDateOptionsWithCopyDocs} />
                                </>
                            )}

                            {isCollection && (
                                <>
                                    <Grid size={{ xs: 6 }}>
                                        <VirtualizedAutocomplete
                                            isMultiple={false} isObject={false} isWritable={isWritable}
                                            isLocked={locked("tenor_type")} filterOn="Tenor"
                                            options={collectionTypeOptions} selectedOptions={formData.tenor_type || null}
                                            handleSelectChange={(_, v) => {
                                                handleChange("tenor_type", v);
                                                setFormData(prev => ({ ...prev, usance_days: null, from_date_field: null }));
                                            }}
                                        />
                                    </Grid>
                                    {tenorType === "D/A" && (
                                        <>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField label="Acceptance Days" type="number" required disabled={locked("usance_days")} value={formData.usance_days ?? ""} onChange={handleDaysChange} size="small" slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }} onKeyDown={numericKeyDown} />
                                            </Grid>
                                            <FromDateDropdown options={fromDateOptionsNoCopyDocs} />
                                        </>
                                    )}
                                </>
                            )}

                        </Grid>
                    </form>
                </AccordionDetails>
            </Accordion>

            <Dialog open={showSblcWarningModal} onClose={() => setShowSblcWarningModal(false)}>
                <DialogContent><DialogContentText className="text-center">{sblcWarningMessage}</DialogContentText></DialogContent>
                <DialogActions><Button type="button" className="btn btn-primary" variant="contained" style={{ width: "fit-content !important" }} onClick={() => setShowSblcWarningModal(false)}>Ok</Button></DialogActions>
            </Dialog>

            <Dialog open={showSblcInvalidModal} onClose={() => setShowSblcInvalidModal(false)}>
                <DialogContent><DialogContentText className="text-center">The validity date for the selected SBLC has passed. It is still allowed to be used, but please verify with your team before proceeding.</DialogContentText></DialogContent>
                <DialogActions><Button type="button" className="btn btn-primary" variant="contained" style={{ width: "fit-content !important" }} onClick={() => setShowSblcInvalidModal(false)}>Ok</Button></DialogActions>
            </Dialog>
        </>
    );
});

export default memo(LinkedSellingPaymentTerms);